import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { CheckCircle } from 'lucide-react';
import { equipmentCategories } from '../data/mock';

const EquipmentGrid = ({ onQuoteClick }) => {
  const navigate = useNavigate();

  return (
    <section id="equipment" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">
            Compare Our Final Pricing
          </h2>
          <p className="text-sm text-gray-500 tracking-wide" data-testid="pricing-subtitle">
            No surprises. No add-ons.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {equipmentCategories
            .filter((c) => c.featured)
            .sort((a, b) => (a.featuredOrder ?? a.id) - (b.featuredOrder ?? b.id))
            .slice(0, 6)
            .map((category) => (
            <Card 
              key={category.id} 
              className="h-full flex flex-col overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-2 hover:border-orange-600"
            >
              <div className="relative h-72 overflow-hidden bg-white">
                <img 
                  src={category.image} 
                  alt={category.name}
                  className="w-full h-full object-contain p-3 transition-transform duration-300 hover:scale-105"
                />
                {category.popular && (
                  <Badge className="absolute top-4 right-4 bg-orange-600 text-white">
                    Popular
                  </Badge>
                )}
              </div>
              
              <CardHeader>
                <CardTitle className="text-xl font-bold text-gray-900">
                  {category.displayName || category.name}
                </CardTitle>
                <CardDescription className="text-gray-600">
                  {category.description}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="flex-grow">
                {category.pricing && (
                  <div className="mb-4 bg-orange-50 p-4 rounded-lg">
                    <div className="text-sm font-semibold text-gray-700 mb-2">Cooper Equipment Rentals:</div>
                    <div className="grid grid-cols-3 gap-2 text-center mb-3">
                      <div>
                        <div className="text-xs text-gray-600">Daily</div>
                        <div className="font-bold text-orange-600">${category.pricing.daily}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-600">Weekly</div>
                        <div className="font-bold text-orange-600">${category.pricing.weekly}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-600">Monthly</div>
                        <div className="font-bold text-orange-600">${category.pricing.monthly}</div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 border-t pt-2">
                      vs. Other Guys: ${category.pricing.competitor.daily} / ${category.pricing.competitor.weekly} / ${category.pricing.competitor.monthly}
                    </div>
                  </div>
                )}
                <ul className="space-y-2">
                  {category.features.map((feature, index) => (
                    <li key={index} className="flex items-start text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              
              <CardFooter className="flex gap-2 mt-auto">
                <Button 
                  onClick={() => navigate('/equipment')}
                  variant="outline"
                  className="flex-1 border-2 border-orange-600 text-orange-600 hover:bg-orange-50"
                >
                  View Equipment
                </Button>
                <Button 
                  onClick={onQuoteClick}
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white transition-colors"
                >
                  Get Quote
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* CTA Banner */}
        <div className="mt-16 bg-gradient-to-r from-orange-600 to-orange-700 rounded-2xl p-8 md:p-12 text-center text-white shadow-xl">
          <h3 className="text-3xl font-bold mb-4">
            Can't Find What You Need?
          </h3>
          <p className="text-lg mb-6 text-orange-100">
            We carry a wide range of additional equipment. Contact us for custom requirements.
          </p>
          <Button 
            onClick={onQuoteClick}
            size="lg"
            className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-6 text-lg font-semibold transition-all"
          >
            Contact Our Team
          </Button>
        </div>
      </div>
    </section>
  );
};

export default EquipmentGrid;
